// =====================================================
// 포인트 사용 관리 탭 — point_usage_type 사용처/필요 포인트 관리
// =====================================================
import { useState, useEffect } from 'react'
import { getApiUrl, fetchWithAuth } from '@/api'

interface UsageType {
  id: number
  name: string
  required_points: number
  stock: number | null
  used_count: number
  remaining_count: number | null
  is_active: number
}

const LIMIT = 10

export default function UsageTypeTab() {
  const [usageTypes, setUsageTypes] = useState<UsageType[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  // 신규 등록
  const [newName, setNewName] = useState('')
  const [newPoints, setNewPoints] = useState('')
  const [newStock, setNewStock] = useState('')
  const [creating, setCreating] = useState(false)
  const [createMsg, setCreateMsg] = useState('')

  // 행별 수정 입력값
  const [inputs, setInputs] = useState<Record<number, { name: string; points: string; stock: string }>>({})
  const [savingId, setSavingId] = useState<number | null>(null)
  const [msg, setMsg] = useState<{ id: number; text: string; ok: boolean } | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<UsageType | null>(null)

  const fetchUsageTypes = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/point/usage-types'))
      if (!res.ok) throw new Error()
      const json = await res.json()
      const list: UsageType[] = json.usage_types ?? []
      setUsageTypes(list)
      // 등록/삭제로 건수가 줄면 마지막 페이지가 사라질 수 있어 범위를 다시 맞춘다.
      setPage(p => Math.min(p, Math.max(1, Math.ceil(list.length / LIMIT))))
      // 수량 칸에는 '남은 수량'을 표시한다. (저장 시 사용 수량을 더해 총 수량으로 환산)
      setInputs(Object.fromEntries(list.map(u => [
        u.id,
        { name: u.name, points: String(u.required_points), stock: u.remaining_count === null ? '' : String(u.remaining_count) },
      ])))
    } catch {
      alert('사용처 목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsageTypes() }, [])

  const handleCreate = async () => {
    const name = newName.trim()
    const points = Number(newPoints.trim())
    const stock = newStock.trim() === '' ? null : Number(newStock.trim())
    if (!name) { setCreateMsg('사용처 이름을 입력해주세요.'); return }
    if (!newPoints.trim() || !Number.isInteger(points) || points < 1) {
      setCreateMsg('필요 포인트는 1 이상의 정수로 입력해주세요.')
      return
    }
    if (stock !== null && (!Number.isInteger(stock) || stock < 0)) {
      setCreateMsg('수량은 0 이상의 정수로 입력해주세요. (비워두면 무제한)')
      return
    }
    setCreating(true)
    setCreateMsg('')
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/point/admin/usage-types'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, required_points: points, stock, is_active: 1 }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json?.detail?.message ?? json?.detail ?? '등록 실패')
      setNewName('')
      setNewPoints('')
      setNewStock('')
      fetchUsageTypes()
    } catch (e: any) {
      setCreateMsg(e.message ?? '등록에 실패했습니다.')
    } finally {
      setCreating(false)
    }
  }

  const patchUsageType = async (
    id: number,
    body: { name?: string; required_points?: number; stock?: number | null; is_active?: number },
    successText = '저장되었습니다.',
  ) => {
    setSavingId(id)
    setMsg(null)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/point/admin/usage-types/${id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json?.detail?.message ?? json?.detail ?? '저장 실패')
      setUsageTypes(prev => prev.map(u => (u.id === id ? {
        ...u,
        name: json.name,
        required_points: json.required_points,
        stock: json.stock,
        used_count: json.used_count,
        remaining_count: json.remaining_count,
        is_active: json.is_active,
      } : u)))
      setInputs(prev => ({
        ...prev,
        [id]: { name: json.name, points: String(json.required_points), stock: json.remaining_count === null ? '' : String(json.remaining_count) },
      }))
      setMsg({ id, text: successText, ok: true })
    } catch (e: any) {
      setMsg({ id, text: e.message ?? '저장에 실패했습니다.', ok: false })
    } finally {
      setSavingId(null)
    }
  }

  const handleSaveRow = (u: UsageType) => {
    const input = inputs[u.id]
    if (!input) return
    const name = input.name.trim()
    const points = Number(input.points.trim())
    const remaining = input.stock.trim() === '' ? null : Number(input.stock.trim())
    if (!name) { setMsg({ id: u.id, text: '이름을 입력해주세요.', ok: false }); return }
    if (!input.points.trim() || !Number.isInteger(points) || points < 1) {
      setMsg({ id: u.id, text: '필요 포인트는 1 이상의 정수로 입력해주세요.', ok: false })
      return
    }
    if (remaining !== null && (!Number.isInteger(remaining) || remaining < 0)) {
      setMsg({ id: u.id, text: '수량은 0 이상의 정수로 입력해주세요. (비워두면 무제한)', ok: false })
      return
    }
    // 입력값은 남은 수량이므로 사용 수량을 더해 총 수량으로 저장한다.
    patchUsageType(u.id, { name, required_points: points, stock: remaining === null ? null : remaining + u.used_count })
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    const target = deleteTarget
    setDeleteTarget(null)
    setSavingId(target.id)
    setMsg(null)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/point/admin/usage-types/${target.id}`), {
        method: 'DELETE',
      })
      if (!res.ok) {
        const json = await res.json()
        throw new Error(json?.detail?.message ?? json?.detail ?? '삭제 실패')
      }
      fetchUsageTypes()
    } catch (e: any) {
      setMsg({ id: target.id, text: e.message ?? '삭제에 실패했습니다. (대기 중인 사용 신청이 있으면 비활성화를 이용하세요)', ok: false })
    } finally {
      setSavingId(null)
    }
  }

  const totalPages = Math.ceil(usageTypes.length / LIMIT)
  const pagedUsageTypes = usageTypes.slice((page - 1) * LIMIT, page * LIMIT)

  return (
    <>
      {/* 신규 등록 */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
        <p className="m-0 mb-3 text-[13px] font-bold text-[#5a3d32]">사용처 등록</p>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={newName}
            onChange={e => { setNewName(e.target.value); setCreateMsg('') }}
            placeholder="사용처 이름 (예: 문구상품권 10,000원)"
            className="w-72 h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]"
          />
          <input
            type="number"
            min={1}
            value={newPoints}
            onChange={e => { setNewPoints(e.target.value); setCreateMsg('') }}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            placeholder="필요 마루"
            className="w-28 h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] text-right outline-none focus:border-[#887267]"
          />
          <span className="text-[13px] text-gray-500">마루</span>
          <input
            type="number"
            min={0}
            value={newStock}
            onChange={e => { setNewStock(e.target.value); setCreateMsg('') }}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            placeholder="수량"
            className="w-28 h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] text-right outline-none focus:border-[#887267]"
          />
          <span className="text-[13px] text-gray-500">개</span>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="h-9 px-4 bg-[#887267] text-white rounded-lg text-[13px] font-semibold hover:bg-[#6b5248] transition-colors disabled:opacity-50"
          >
            {creating ? '등록 중...' : '+ 등록'}
          </button>
        </div>
        <p className="m-0 mt-2 text-[12px] text-gray-400">수량을 비워두면 무제한으로 등록됩니다.</p>
        {createMsg && <p className="m-0 mt-2 text-[12px] text-red-400">{createMsg}</p>}
      </div>

      {/* 목록 */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
        <div className="px-5 py-4 border-b border-[#f5f2f0] flex items-end justify-between gap-4">
          <div>
            <p className="m-0 text-[14px] font-bold text-[#5a3d32]">사용처 목록</p>
            <p className="m-0 mt-0.5 text-[12px] text-gray-400">
              키오스크 포인트 사용 화면에 노출되는 항목입니다. 비활성화하면 노출되지 않습니다.
              남은 수량이 0이면 신청이 막히고, 신청을 반려하면 수량이 다시 돌아옵니다.
            </p>
          </div>
          <div className="text-[13px] text-gray-400 font-medium whitespace-nowrap">
            총 <span className="text-[#887267] font-bold">{usageTypes.length}</span>건
          </div>
        </div>

        {loading ? (
          <div className="min-h-[200px] flex items-center justify-center text-gray-300 text-[13px]">불러오는 중...</div>
        ) : usageTypes.length === 0 ? (
          <div className="min-h-[160px] flex items-center justify-center text-gray-300 text-[13px]">등록된 사용처가 없습니다.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">사용처</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[200px]">필요 포인트</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[180px]">남은 수량</th>
                <th className="text-center px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">노출</th>
                <th className="text-center px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[140px]">관리</th>
              </tr>
            </thead>
            <tbody>
              {pagedUsageTypes.map(u => (
                <tr key={u.id} className={`border-t border-[#f5f2f0] transition-colors ${u.is_active === 1 ? 'hover:bg-[#fdf8f0]' : 'bg-[#fafafa] opacity-60'}`}>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={inputs[u.id]?.name ?? ''}
                      onChange={e => { setInputs(prev => ({ ...prev, [u.id]: { ...prev[u.id], name: e.target.value } })); setMsg(null) }}
                      className="w-full max-w-[320px] h-8 px-2 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]"
                    />
                    {msg?.id === u.id && (
                      <p className={`m-0 mt-1 text-[11px] ${msg.ok ? 'text-green-600' : 'text-red-400'}`}>{msg.text}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        value={inputs[u.id]?.points ?? ''}
                        onChange={e => { setInputs(prev => ({ ...prev, [u.id]: { ...prev[u.id], points: e.target.value } })); setMsg(null) }}
                        onKeyDown={e => e.key === 'Enter' && handleSaveRow(u)}
                        className="w-24 h-8 px-2 border border-[#e8e0dc] rounded-lg text-[13px] text-right outline-none focus:border-[#887267]"
                      />
                      <span className="text-[12px] text-gray-400">마루</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        value={inputs[u.id]?.stock ?? ''}
                        onChange={e => { setInputs(prev => ({ ...prev, [u.id]: { ...prev[u.id], stock: e.target.value } })); setMsg(null) }}
                        onKeyDown={e => e.key === 'Enter' && handleSaveRow(u)}
                        placeholder="무제한"
                        className="w-20 h-8 px-2 border border-[#e8e0dc] rounded-lg text-[13px] text-right outline-none focus:border-[#887267]"
                      />
                      <span className="text-[12px] text-gray-400">개</span>
                      <span className="text-[11px] text-gray-400 whitespace-nowrap">
                        {u.stock === null ? '무제한' : `사용 ${u.used_count}개`}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => patchUsageType(u.id, { is_active: u.is_active === 1 ? 0 : 1 }, u.is_active === 1 ? '비활성화되었습니다.' : '활성화되었습니다.')}
                      disabled={savingId === u.id}
                      className={`text-[11px] px-3 py-1 rounded-full font-semibold transition-colors disabled:opacity-50 ${
                        u.is_active === 1
                          ? 'bg-[#e8f5e9] text-[#388e3c] hover:bg-[#dcedc8]'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                    >
                      {u.is_active === 1 ? '노출' : '숨김'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <button
                      onClick={() => handleSaveRow(u)}
                      disabled={savingId === u.id}
                      className="h-8 px-3 bg-[#887267] text-white rounded-lg text-[12px] font-semibold hover:bg-[#6b5248] transition-colors disabled:opacity-50 mr-1"
                    >
                      {savingId === u.id ? '저장중' : '저장'}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(u)}
                      disabled={savingId === u.id}
                      className="h-8 px-3 border border-red-200 text-red-400 rounded-lg text-[12px] hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-1.5 mt-5">
          <button
            onClick={() => setPage(p => p - 1)}
            disabled={page === 1}
            className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
          >‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${
                page === p
                  ? 'bg-[#887267] text-white border-[#887267]'
                  : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
              }`}
            >{p}</button>
          ))}
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page === totalPages}
            className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
          >›</button>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[360px] mx-4 p-6">
            <h3 className="text-[16px] font-bold text-[#2a1f1a] mb-2">사용처를 삭제하시겠습니까?</h3>
            <p className="text-[13px] text-gray-400 mb-5">
              "{deleteTarget.name}" — 지난 사용 내역은 그대로 남습니다. 처리 대기 중인 사용 신청이 있으면 삭제할 수 없습니다.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]">취소</button>
              <button onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 text-[13px] font-semibold">삭제</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
